import React from 'react';
import { X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { appDesignTokens as tokens } from './appDesignTokens';
import { useTheme } from '../context/ThemeContext';

export default function DidYouApplyModal({ isOpen, onClose, onConfirm, jobTitle, companyName }) {
  const { isDark } = useTheme();
  if (!isOpen) return null;

  const bg = isDark ? '#0f1322' : tokens.colors.surface;
  const border = isDark ? 'rgba(255,255,255,0.09)' : tokens.colors.border;
  const text = isDark ? 'rgba(255,255,255,0.92)' : tokens.colors.textPrimary;
  const muted = isDark ? 'rgba(255,255,255,0.55)' : tokens.colors.textSecondary;
  const closeBg = isDark ? 'rgba(255,255,255,0.07)' : tokens.colors.surfaceSubtle;
  const closeIcon = isDark ? 'rgba(255,255,255,0.45)' : tokens.colors.textMuted;
  const iconRingBg = isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.08)';
  const iconRingBorder = isDark ? 'rgba(245,158,11,0.2)' : 'rgba(37,99,235,0.15)';
  const accentColor = isDark ? '#F59E0B' : tokens.colors.accent;
  const noButtonBg = isDark ? 'rgba(255,255,255,0.05)' : tokens.colors.surface;
  const noButtonColor = isDark ? 'rgba(255,255,255,0.6)' : tokens.colors.textSecondary;

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[1200] flex items-center justify-center p-4"
        style={{
          background: isDark ? 'rgba(5,8,20,0.65)' : 'rgba(15,23,42,0.38)',
          backdropFilter: 'blur(8px)',
          fontFamily: tokens.fonts.body,
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="w-full max-w-md overflow-hidden relative"
          style={{
            background: bg,
            border: `1px solid ${border}`,
            borderRadius: tokens.radius.modal,
            boxShadow: isDark
              ? '0 24px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)'
              : tokens.shadow.modal,
          }}
        >
          <button
            onClick={onClose}
            className="absolute right-4 top-4 transition-colors"
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              border: 'none',
              background: closeBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={18} color={closeIcon} />
          </button>

          <div className="p-8 flex flex-col items-center text-center">
            <div
              className="w-20 h-20 flex items-center justify-center mb-6"
              style={{
                borderRadius: 22,
                background: iconRingBg,
                border: `1px solid ${iconRingBorder}`,
                boxShadow: isDark
                  ? '0 10px 30px rgba(245,158,11,0.08)'
                  : '0 10px 30px rgba(37,99,235,0.08)',
              }}
            >
              <Check size={34} color={accentColor} strokeWidth={2.4} />
            </div>

            <h2
              className="mb-2"
              style={{
                fontFamily: tokens.fonts.heading,
                fontSize: 28,
                fontWeight: 800,
                lineHeight: 1.15,
                color: text,
              }}
            >
              Did you apply?
            </h2>
            <p
              className="text-sm mb-8 px-4"
              style={{
                color: muted,
                lineHeight: 1.6,
              }}
            >
              Let us know so we can help you track and refine future recommendations for you.
            </p>

            <div className="flex flex-col w-full gap-3">
              <button
                onClick={() => onConfirm(true)}
                className="w-full py-4 transition-all active:scale-[0.98]"
                style={{
                  borderRadius: 16,
                  background: isDark
                    ? 'linear-gradient(135deg,#F59E0B,#D97706)'
                    : tokens.gradients.primaryButton,
                  color: isDark ? '#1a1612' : '#ffffff',
                  fontWeight: 800,
                  fontSize: 14,
                  border: 'none',
                  boxShadow: isDark
                    ? '0 4px 18px rgba(245,158,11,0.3)'
                    : tokens.shadow.primaryButton,
                  cursor: 'pointer',
                }}
              >
                Yes, I applied!
              </button>
              <button
                onClick={() => onConfirm(false)}
                className="w-full py-4 transition-all active:scale-[0.98]"
                style={{
                  borderRadius: 16,
                  background: noButtonBg,
                  color: noButtonColor,
                  fontWeight: 700,
                  fontSize: 14,
                  border: `1px solid ${border}`,
                  cursor: 'pointer',
                }}
              >
                No, I didn't apply
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
